/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	Pressable,
	View,
	Text,
	ErrorText,
	TextField,
	Image,
	Button,
	Icon,
} from '@components/atoms'
import { Modal } from '@components/molecules'
import { Footer } from '@components/systems'
import loginBg from '@public/images/pages/customerLogin/login-bg.jpg'

import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { useNavigate } from 'react-router-dom'
import { Validator, ValidatorResultType } from '@validators'
import { grey } from '@mui/material/colors'

const CustomerForgotPw = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// redux state
	const forgotPasswordErrorMessage: string = useAppSelector(
		state => state.customerAuth.forgotPasswordErrorMessage,
	)
	const showForgotPasswordModal: boolean = useAppSelector(
		state => state.customerAuth.showForgotPasswordModal,
	)

	// state
	const [email, setEmail] = React.useState<string>('')

	const onEmailChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const str = e.target.value

			setEmail(str)
		},
		[email],
	)

	const onSendToEmailClick = React.useCallback(() => {
		const res: ValidatorResultType = Validator.customer({
			email: email,
		})

		if (!res.email.isValid) {
			reduxDispatch(customerAuthActions.FORGOT_PASSWORD_ERROR_MESSAGE(res.email.message))
		} else {
			reduxDispatch(customerAuthActions.FORGOT_PASSWORD({ email: email }))
		}
	}, [email])

	return (
		<View css={style}>
			<View className="customerLoginOuterView">
				<View className="loginPaper">
					<View className="loginImageView">
						<Image src={loginBg} className="loginImage" />
					</View>

					<View className="loginInputView">
						<Pressable className="backButton" onClick={() => navigate('/customer/login')}>
							<Icon name="arrow-left" className="backButtonIcon" />
						</Pressable>
						<Text category="h4" className="inputTitle">
							Forgot Password
						</Text>
						<View className="inputView">
							<TextField
								type="text"
								label="Email"
								size="small"
								defaultValue={email}
								onChange={onEmailChange}
							/>
						</View>

						<View className="loginButtonDiv">
							{forgotPasswordErrorMessage !== '' ? (
								<ErrorText className="errorMsg">{forgotPasswordErrorMessage}</ErrorText>
							) : null}

							<Button
								onClick={onSendToEmailClick}
								className="loginButton"
								variant="contained"
							>
								Send to Email
							</Button>
						</View>
					</View>
				</View>

				<View className="loginBackgroundView">
					<View className="loginBgView"></View>
					<Image src={loginBg} className="loginBackground" />
				</View>
			</View>

			<Footer />

			<Modal
				target="newGroup"
				title="Password reset email sent"
				show={showForgotPasswordModal}
				onCloseModal={() => {
					reduxDispatch(customerAuthActions.SHOW_FORGOT_PASSWORD_MODAL(false))
					navigate('/customer/login')
				}}
				closeStatus="inherit"
				showSubmit={false}
				closeTitle="Close"
			>
				<View css={{ width: '30rem' }}>
					<Text category="p2">An email has been sent to reset your password.</Text>
					<Text category="p2">Please check the email you entered.</Text>
					<Text category="p2">
						If you are unable to receive the email, please contact us via CONTACT.
					</Text>
				</View>
			</Modal>
		</View>
	)
}

export default CustomerForgotPw

const style = css({
	position: 'relative',
	width: '100%',

	'.customerLoginOuterView': {
		position: 'relative',
		height: 'calc(100vh - 10rem)',
		width: '100%',
		overflow: 'hidden',

		'.loginPaper': {
			position: 'absolute',
			zIndex: 5,
			display: 'flex',
			width: '60rem',
			height: '35rem',
			top: 0,
			bottom: 0,
			right: 0,
			left: 0,
			margin: 'auto',
			background: '#FFF',
			borderRadius: '10px',
			overflow: 'hidden',

			'.loginImageView': {
				width: 'calc(100% - 25rem)',
				overflow: 'hidden',

				'.loginImage': {
					width: 'auto',
					height: '100%',
					marginLeft: '-7rem',
				},
			},

			'.loginInputView': {
				width: '25rem',
				padding: '2rem',
				textAlign: 'left',

				'.backButton': {
					'.backButtonIcon': {},
				},

				'.inputTitle': {
					textAlign: 'center',
				},

				'.inputView': {
					width: '100%',
					marginBottom: '1.2rem',
				},

				'.input': {
					width: '100%',
				},

				'.loginButtonDiv': {
					position: 'relative',
					width: '100%',
					marginBottom: '0.8rem',
					paddingTop: 0,

					'.idSaveView': {
						display: 'flex',
						alignItems: 'center',
					},

					'.loginButton': {
						width: '100%',
						height: '2.6rem',
						background: grey[900],
					},
				},
			},
		},

		'.loginBackgroundView': {
			position: 'absolute',
			zIndex: 0,
			top: 0,
			bottom: 0,
			width: '100%',
			height: 'auto',
			overflow: 'hidden',

			'.loginBgView': {
				position: 'absolute',
				zIndex: 1,
				top: 0,
				bottom: 0,
				width: '100%',
				height: '100%',
				background: grey[900],
				opacity: 0.5,
			},

			'.loginBackground': {
				height: 'auto',
				width: '110%',
				marginTop: '-1rem',
				marginLeft: '-1rem',
				filter: 'blur(10px)',
			},
		},
	},

	'@media (max-width: 1050px)': {
		'.customerLoginOuterView': {
			'.loginPaper': {
				width: 'calc(100% - 4rem)',
			},
		},
	},

	'@media (max-width: 950px)': {
		'.customerLoginOuterView': {
			'.loginPaper': {
				'.loginImageView': {
					width: 'calc(100% - 40vw)',

					'.loginImage': {
						height: '120%',
						marginLeft: '-20vw',
					},
				},

				'.loginInputView': {
					width: '40vw',
				},
			},
			'.loginBackgroundView': {
				'.loginBackground': {
					width: '150%',
				},
			},
		},
	},

	'@media (max-width: 760px)': {
		'.customerLoginOuterView': {
			'.loginPaper': {
				maxWidth: '28rem',
				width: 'calc(100vw - 4rem)',

				'.loginImageView': {
					display: 'none',
				},

				'.loginInputView': {
					width: '100%',
				},
			},
		},
	},

	'@media (max-width: 400px)': {
		'.customerLoginOuterView': {
			'.loginPaper': {
				width: 'calc(100vw - 1rem)',
			},
		},
	},
})
